import React from "react";
import {
  Card,
  Input,
  Radio,
  Button,
  Upload,
  Row,
  Col,
  message,
  Select,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  CloseOutlined,
  CheckOutlined,
} from "@ant-design/icons";

interface Answer {
  id: string;
  text: string;
}

export interface QuestionData {
  id: string;
  prompt: string;
  answers: Answer[];
  correctId: string | null;
  type: "multiple";
  image?: string;
  function?: number;
  task?: number;
  role?: number;
  difficulty?: string;
  weight?: number;
}

interface Props {
  data: QuestionData;
  onChange: (updated: QuestionData) => void;
  onRemove?: () => void;
  functions?: Array<{ value: number; label: string }>;
  tasks?: Array<{ value: number; label: string }>;
  roles?: Array<{ value: number; label: string }>;
  readOnly?: boolean;
  onFunctionChange?: (functionId: number | undefined) => void;
}

const QuestionCard: React.FC<Props> = ({
  data,
  onChange,
  onRemove,
  functions = [],
  tasks = [],
  roles = [],
  readOnly = false,
  onFunctionChange,
}) => {
  const update = (patch: Partial<QuestionData>) =>
    onChange({ ...data, ...patch });

  /* answer helpers */
  const setAnswerText = (id: string, text: string) =>
    update({
      answers: data.answers.map((a) => (a.id === id ? { ...a, text } : a)),
    });

  const addAnswer = () =>
    update({
      answers: [...data.answers, { id: Date.now().toString(), text: "" }],
    });

  const deleteAnswer = (id: string) =>
    update({
      answers: data.answers.filter((a) => a.id !== id),
      correctId: data.correctId === id ? null : data.correctId,
    });

  const markCorrect = (id: string) => update({ correctId: id });

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result)); // full data: URI
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /* image upload */
  const beforeUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      message.error("Only image files accepted");
      return Upload.LIST_IGNORE;
    }
    try {
      const b64 = await fileToBase64(file);
      update({ image: b64 });
    } catch (err) {
      message.error("Failed to read file");
    }
    return Upload.LIST_IGNORE;
  };

  const removeImage = () => {
    update({ image: undefined });
  };

  const getImageSrc = () =>
    typeof data.image === "string" ? data.image : null;

  return (
    <Card
      bodyStyle={{ padding: 24 }}
      style={{ borderRadius: 12, marginBottom: 24 }}
    >
      {/* prompt */}
      <Input
        value={data.prompt}
        placeholder="Question text"
        onChange={
          readOnly ? undefined : (e) => update({ prompt: e.target.value })
        }
        style={prompt}
        readOnly={readOnly}
      />

      <Row gutter={24} style={{ marginTop: 24 }}>
        {/* answers side */}
        <Col flex="1 1 65%">
          {data.answers.map((a) => (
            <Row
              key={a.id}
              align="middle"
              gutter={12}
              style={{ marginBottom: 12 }}
            >
              <Col flex="auto">
                <div
                  style={{
                    ...answerWrapper,
                    border:
                      data.correctId === a.id
                        ? "2px solid #0f1b52"
                        : "1px dashed #B3B3B3",
                  }}
                >
                  <Radio
                    checked={data.correctId === a.id}
                    onChange={readOnly ? undefined : () => markCorrect(a.id)}
                    style={{ marginRight: 8 }}
                    disabled={readOnly}
                  />
                  <Input
                    value={a.text}
                    placeholder="Answer"
                    onChange={
                      readOnly
                        ? undefined
                        : (e) => setAnswerText(a.id, e.target.value)
                    }
                    bordered={false}
                    style={answerInput}
                    readOnly={readOnly}
                  />
                  {!readOnly && (
                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => deleteAnswer(a.id)}
                      type="text"
                    />
                  )}
                </div>
              </Col>

              <Col flex="110px">
                {data.correctId === a.id ? (
                  <Button
                    icon={<CheckOutlined />}
                    style={markedBtn}
                    type="primary"
                    block
                  >
                    Correct
                  </Button>
                ) : (
                  <Button
                    onClick={readOnly ? undefined : () => markCorrect(a.id)}
                    block
                    style={setBtn}
                    disabled={readOnly}
                  >
                    Set as correct
                  </Button>
                )}
              </Col>
            </Row>
          ))}

          {!readOnly && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              block
              onClick={addAnswer}
            >
              Add another answer
            </Button>
          )}
        </Col>

        {/* right side panel */}
        <Col flex="220px">
          <Input value="Multiple choice" disabled style={typeTag} />

          {!readOnly && (
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={readOnly ? () => Upload.LIST_IGNORE : beforeUpload}
              disabled={readOnly}
            >
              {data.image ? (
                <div style={{ position: "relative" }}>
                  <img
                    src={getImageSrc()!}
                    alt="q"
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                  {!readOnly && (
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage();
                      }}
                    />
                  )}
                </div>
              ) : (
                <Button icon={<UploadOutlined />} block>
                  Attach an image
                </Button>
              )}
            </Upload>
          )}

          {!readOnly && onRemove && (
            <Button
              danger
              icon={<DeleteOutlined />}
              block
              style={{ marginTop: 16 }}
              onClick={onRemove}
            >
              Delete question
            </Button>
          )}
        </Col>
      </Row>

      {/* meta-row */}
      <Row gutter={12} style={{ marginTop: 24 }}>
        <Col>
          <Select
            value={data.function}
            placeholder="Function *"
            style={{
              ...metaSelect,
              borderColor: !data.function && !readOnly ? "#ff4d4f" : undefined
            }}
            options={functions}
            onChange={readOnly ? undefined : (v) => {
              update({ function: v, task: undefined }); // Reset task when function changes
              if (onFunctionChange) {
                onFunctionChange(v);
              }
            }}
            allowClear
            disabled={readOnly}
            dropdownStyle={{ minWidth: 250 }}
            notFoundContent="No functions available"
          />
        </Col>
        <Col>
          <Select
            value={data.task}
            placeholder="Task (optional)"
            style={metaSelect}
            className="task-select"
            options={tasks}
            onChange={readOnly ? undefined : (v) => update({ task: v })}
            allowClear
            disabled={readOnly || !data.function}
            notFoundContent={!data.function ? "Select a function first" : "No tasks found"}
            dropdownStyle={{ minWidth: 400 }}
          />
        </Col>
        <Col>
          <Select
            value={data.role}
            placeholder="Role"
            style={metaSelect}
            options={roles}
            onChange={readOnly ? undefined : (v) => update({ role: v })}
            allowClear
            disabled={readOnly}
            notFoundContent="No roles available"
          />
        </Col>
        <Col>
          <Select
            value={data.difficulty}
            placeholder="Difficulty"
            style={metaSelect}
            options={[
              { value: "bronze", label: "Difficulty: Bronze" },
              { value: "silver", label: "Difficulty: Silver" },
              { value: "gold", label: "Difficulty: Gold" },
            ]}
            onChange={readOnly ? undefined : (v) => update({ difficulty: v })}
            disabled={readOnly}
            notFoundContent="No difficulty levels available"
          />
        </Col>
        <Col>
          <Select
            value={data.weight}
            placeholder="Weight"
            style={metaSelect}
            options={[1, 2, 3, 4, 5].map((n) => ({
              value: n,
              label: `Weight: ${n}`,
            }))}
            onChange={readOnly ? undefined : (v) => update({ weight: v })}
            disabled={readOnly}
            notFoundContent="No weight options available"
          />
        </Col>
      </Row>
    </Card>
  );
};

const prompt: React.CSSProperties = {
  background: "#E9EDFF",
  borderRadius: 9999,
  fontWeight: 600,
};

const answerWrapper: React.CSSProperties = {
  borderRadius: 9999,
  padding: "6px 12px",
  display: "flex",
  alignItems: "center",
};

const answerInput: React.CSSProperties = {
  background: "transparent",
  border: "none",
  flex: 1,
};

const typeTag: React.CSSProperties = {
  width: "100%",
  marginBottom: 16,
  background: "#E9EDFF",
  borderRadius: 9999,
  textAlign: "center",
  fontWeight: 600,
};

const metaSelect: React.CSSProperties = {
  minWidth: 180,
  borderRadius: 9999,
  background: "#E9EDFF",
};

const answerBtnWidth = 120;

const setBtn: React.CSSProperties = {
  width: answerBtnWidth,
  borderRadius: 9999,
  justifyContent: "center",
};

const markedBtn: React.CSSProperties = {
  ...setBtn,
  background: "#0f1b52",
  border: "none",
  color: "#fff",
};

export default QuestionCard;

// Add global styles for consistent Task dropdown appearance
const style = document.createElement('style');
style.textContent = `
  .task-select.ant-select-disabled .ant-select-selector {
    background-color: #E9EDFF !important;
    border-color: #d9d9d9 !important;
  }
`;
document.head.appendChild(style);
